import { apiClient } from './client';
import { PaginatedResponse } from '../types/pagination';
import {
  AdmissionDashboardStats,
  Application,
  ApplicationRemark,
  ApplicationStatus,
  ApplicationTimelineEntry,
  CourseDocumentRequirement,
  DocumentChecklistItem,
  Qualification,
  ReadyToFlyEligibility,
} from '../types/academic';

export const listQualifications = async (studentId: number): Promise<Qualification[]> => {
  const { data } = await apiClient.get(`/students/${studentId}/qualifications`);
  return data.data;
};

export interface CreateQualificationPayload {
  name: string;
  institution: string;
  country?: string;
  yearCompleted?: number;
  gradeOrGpa?: string;
  majorSubject?: string;
  fromDate?: string;
  toDate?: string;
  academicLevelId?: number;
  countryId?: number;
  gpa?: number;
  completionStatus?: string;
}

export const createQualification = async (
  studentId: number,
  payload: CreateQualificationPayload,
): Promise<Qualification> => {
  const { data } = await apiClient.post(`/students/${studentId}/qualifications`, payload);
  return data.data;
};

export const updateQualification = async (
  qualificationId: number,
  payload: Partial<CreateQualificationPayload>,
): Promise<Qualification> => {
  const { data } = await apiClient.patch(`/students/qualifications/${qualificationId}`, payload);
  return data.data;
};

export const deleteQualification = async (qualificationId: number): Promise<void> => {
  await apiClient.delete(`/students/qualifications/${qualificationId}`);
};

export interface ListApplicationsParams {
  page: number;
  pageSize: number;
  studentId?: number;
  universityId?: number;
  status?: ApplicationStatus;
  assignedToId?: number;
}

export const listApplications = async (
  params: ListApplicationsParams,
): Promise<PaginatedResponse<Application>> => {
  const { data } = await apiClient.get('/applications', { params });
  return data;
};

export interface CreateApplicationPayload {
  studentId: number;
  universityId: number;
  courseId: number;
  intakeId?: number;
  notes?: string;
}

export const createApplication = async (payload: CreateApplicationPayload): Promise<Application> => {
  const { data } = await apiClient.post('/applications', payload);
  return data.data;
};

export interface UpdateApplicationPayload {
  intakeId?: number | null;
  offerLetterDate?: string | null;
  coeNumber?: string | null;
  coeDate?: string | null;
  visaStatusId?: number | null;
  visaAppliedDate?: string | null;
  visaDecisionDate?: string | null;
  visaExpiryDate?: string | null;
  visaRemarks?: string | null;
  departureDate?: string | null;
  arrivalDate?: string | null;
  notes?: string | null;
}

export const getApplication = async (id: number): Promise<Application> => {
  const { data } = await apiClient.get(`/applications/${id}`);
  return data.data;
};

export const updateApplication = async (id: number, payload: UpdateApplicationPayload): Promise<Application> => {
  const { data } = await apiClient.patch(`/applications/${id}`, payload);
  return data.data;
};

export const deleteApplication = async (id: number): Promise<void> => {
  await apiClient.delete(`/applications/${id}`);
};

export const changeApplicationStatus = async (
  id: number,
  status: ApplicationStatus,
  remark?: string,
): Promise<Application> => {
  const { data } = await apiClient.post(`/applications/${id}/status`, { status, remark });
  return data.data;
};

export const assignApplication = async (
  id: number,
  userId: number | null,
  reason?: string,
): Promise<Application> => {
  const { data } = await apiClient.post(`/applications/${id}/assign`, { userId, reason });
  return data.data;
};

export const getApplicationTimeline = async (id: number): Promise<ApplicationTimelineEntry[]> => {
  const { data } = await apiClient.get(`/applications/${id}/timeline`);
  return data.data;
};

export const listApplicationRemarks = async (id: number): Promise<ApplicationRemark[]> => {
  const { data } = await apiClient.get(`/applications/${id}/remarks`);
  return data.data;
};

export const addApplicationRemark = async (id: number, remark: string): Promise<ApplicationRemark> => {
  const { data } = await apiClient.post(`/applications/${id}/remarks`, { remark });
  return data.data;
};

export const getApplicationDocumentChecklist = async (id: number): Promise<DocumentChecklistItem[]> => {
  const { data } = await apiClient.get(`/applications/${id}/document-checklist`);
  return data.data;
};

export const getAdmissionDashboard = async (): Promise<AdmissionDashboardStats> => {
  const { data } = await apiClient.get('/applications/dashboard');
  return data.data;
};

export const getReadyToFlyEligibility = async (id: number): Promise<ReadyToFlyEligibility> => {
  const { data } = await apiClient.get(`/applications/${id}/ready-to-fly-eligibility`);
  return data.data;
};

export const listCourseDocumentRequirements = async (courseId: number): Promise<CourseDocumentRequirement[]> => {
  const { data } = await apiClient.get(`/master-data/courses/${courseId}/document-requirements`);
  return data.data;
};

export const addCourseDocumentRequirement = async (
  courseId: number,
  documentTypeId: number,
  isMandatory = true,
): Promise<CourseDocumentRequirement> => {
  const { data } = await apiClient.post(`/master-data/courses/${courseId}/document-requirements`, {
    documentTypeId,
    isMandatory,
  });
  return data.data;
};

export const removeCourseDocumentRequirement = async (requirementId: number): Promise<void> => {
  await apiClient.delete(`/master-data/courses/document-requirements/${requirementId}`);
};
